import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { Moment } from 'moment';
import { ListAdvisorsDataProps } from 'modules/advisors/interfaces';

export enum CloudProviderEnum {
  AWS = 'aws',
  OCI = 'oci',
  MULTICLOUD = 'multicloud',
}

export interface MenuActionItemProps {
  key: string | number;
  id?: string;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
  infoButton?: boolean;
  onClick: () => void;
}

export interface EnvironmentProvider {
  type: CloudProviderEnum;
  region: string;
  default_zone?: string;
  compartment_id: string;
  oci_config_profile_name:
    | 'DEFAULT'
    | 'SKYONECLOUD02'
    | 'SKYONECLOUD03'
    | 'PARCEIRO-SIESA'
    | 'PARCEIRO-DMA'
    | null;
}

export type BehaviorType = 'manual' | 'micro' | 'period' | 'spot_period' | '';

export interface EnvironmentBehavior {
  type: BehaviorType;
  timezone: string;
  comments: string;
}

export interface EnvironmentMaintenanceImmediate {
  active: boolean;
}

export interface EnvironmentMaintenance {
  auto_remove_user_session: EnvironmentMaintenanceRemoveSessionsProps;
  display_warning: EnvironmentMaintenanceDisplayWarningProps;
}

export interface EnvironmentLicenses {
  license_limit: number;
  increase_license_limit_each: number;
  control_licenses_by_client: boolean;
  block_same_user_multiple_session: boolean;
  increase_license_reason: string | null;
  bypass_increase_license_auto_disable: boolean;
}

export interface EnvironmentUserConnection {
  virtualizer:
    | 'connect'
    | 'connect_direct'
    | 'rdp'
    | 'rdp_direct'
    | 'rdp_app'
    | 'tsplus'
    | 'tsplus_remote_app'
    | 'tsplus_all'
    | 'aspw'
    | 'aspw_all';
  service_port: number;
  web_port: number | null;
  quality: 'high' | 'default' | 'low';
  https_proxy: string;
  network_type: 'public' | 'private';
  connect_by_dns_domain_name: boolean;
  allow_client_connection_type: boolean;
  use_ios_by_default: boolean;
  unique_user_session_validation: boolean;
}

export interface EnvironmentScaleInstances {
  main_model_uuid: string;
  backup_model_uuid: string;
  spot_model_uuid: string;
  spot_threshold: number;
  is_creating: boolean;
  num_instances_being_created: number;
}

export interface EnvironmentPublicationScheduleBlocking {
  time: string;
  warning_emails: string;
}

export interface EnvironmentPublication {
  type: ModelTypeEnum;
  limit_instance_blocking_publications: number;
  limit_instance_blocking_publications_daily: number | null | string;
  executed_instance_blocking_publications: number;
  executed_instance_blocking_publications_daily: number;
  publication_can_block_old_instances: boolean;
  schedule_instances_blocking_check: boolean | string;
  schedule_instances_blocking: EnvironmentPublicationScheduleBlocking | null;
}

export interface EnvironmentMetricsSessions {
  total: number;
  active: number;
  forwarded: number;
}

export interface EnvironmentMetricsScaleInstancesByStates {
  turning_on: number;
  blocked: number;
  filled: number;
  unreachable: number;
  preparing: number;
  turned_on: number;
  operational: number;
  stopped: number;
  stopping: number;
  terminating: number;
  error: number;
}

export interface EnvironmentMetricsScaleInstances {
  average_memory_usage: number;
  average_cpu_usage: number;
  average_temperature: number;
  total_instances: number;
  total_instances_by_states: EnvironmentMetricsScaleInstancesByStates;
}

export interface EnvironmentMetrics {
  sessions: EnvironmentMetricsSessions;
  scale_instances: EnvironmentMetricsScaleInstances;
}

export enum EnvironmentStatus {
  DEMONSTRATION = 'demonstration',
  DEPLOYMENT = 'deployment',
  DEVELOPMENT = 'development',
  HOMOLOGATION = 'homologation',
  MIGRATION = 'migration',
  PENDING = 'pending',
  PRODUCTION = 'production',
}

export interface EnvironmentPartner {
  provider: {
    oci_config_profile_name:
      | 'DEFAULT'
      | 'SKYONECLOUD02'
      | 'SKYONECLOUD03'
      | 'PARCEIRO-SIESA'
      | 'PARCEIRO-DMA';
  };
  uuid: string;
  name: string;
}

export interface DeviceValidationSettings {
  device_validation_enabled: boolean;
  environment_admin_pin: string;
}

export interface InstanceObservabilityConfig {
  enabled: boolean;
}

export interface EnvironmentFields {
  name: string;
  environment_uuid: string;
  company: { uuid: string; display_name: string };
  trusted_users: string;
  product: 'autosky' | 'business_one';
  product_type: string;
  partner: EnvironmentPartner;
  provider: EnvironmentProvider;
  behavior: EnvironmentBehavior;
  maintenance: EnvironmentMaintenance;
  in_maintenance: boolean;
  licenses: EnvironmentLicenses;
  user_connection: EnvironmentUserConnection;
  scale_instances: EnvironmentScaleInstances;
  publication: EnvironmentPublication;
  metrics: EnvironmentMetrics;
  zero_trust: ZeroTrustProps;
  user_last_access: null | Date;
  client_apps: {
    registration_source: string;
    registration_mode: 'simple' | 'advanced';
  };
  env_status: EnvironmentStatus;
  completion_date: Date | null;
  completion_date_extendable: boolean;
  completion_date_reason: string;
  completion_date_proposed: Date | null;
  completion_date_change_request: boolean;
  completion_date_request_by: {
    operator_uuid: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  should_apply_advisor_automatically: boolean;
  high_risk_environment: boolean;
  device_validation_settings: DeviceValidationSettings;
  user_access_without_ad: {
    enabled: boolean;
  };
  instance_observability?: InstanceObservabilityConfig;
}

export interface AllEnvironmentsFields {
  environment_name: string;
  environment_uuid: string;
  partner_name: string;
  partner_uuid: string;
  company_name: string;
  company_uuid: string;
  num_clients: number;
  num_users: number;
  operational_instances_count: number;
  sessions_count: number;
  provider_type: string;
  behavior_type: BehaviorType;
  user_last_access: null | Date;
  in_maintenance: boolean;
  env_status: string;
  completion_date_expire_at_days: number;
  license_limit: number;
}

export interface ZeroTrustProps {
  enabled: boolean;
  port: number | null;
  allow_list: string[];
  whitelist_cleanup_interval_days: number;
}

export interface InitialPeriodProps {
  is_active: boolean;
  hour: Date;
  scale_instances_by_licences_margin: string;
  licences_margin_percent: number;
  keep_support_servers: string;
}

export interface FormSecurityValues {
  activate: boolean;
  min_length: number;
  require_upper: boolean;
  require_lower: boolean;
  require_num: boolean;
  require_symbol: boolean;
  expire_passwords: boolean;
  expire_days: number;
  max_auth_tries: number;
  zero_trust: ZeroTrustProps;
  check_history: boolean;
  check_complexity: boolean;
}

export interface InitialEnvironmentFields {
  env_status: EnvironmentStatus | string;
  completion_date: Date | null;
  completion_date_extendable: boolean;
  completion_date_reason: string;
  completion_date_proposed: Date | null;
  completion_date_change_request: boolean;
  in_maintenance: boolean;
  completion_date_request_by: {
    operator_uuid: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  name: string;
  partner_uuid: string;
  easysky_project_id: string;
  trusted_users: string;
  product: string;
  product_type: string;
  provider: EnvironmentProvider;
  behavior: EnvironmentBehavior;
  licenses: EnvironmentLicenses;
  user_connection: EnvironmentUserConnection;
  scale_instances: EnvironmentScaleInstances;
  publication: {
    type: string;
    limit_instance_blocking_publications: number;
    limit_instance_blocking_publications_daily: number | null | string;
    executed_instance_blocking_publications: number;
    executed_instance_blocking_publications_daily: number;
    publication_can_block_old_instances: boolean;
    schedule_instances_blocking_check: string;
    schedule_instances_blocking: {
      time: string | null | Date;
      warning_emails: string;
    };
  };
  client_apps: {
    registration_source: string;
    registration_mode: string;
  };
  advanced_properties?: Record<string, any> | null;
  password_policy: {
    min_length: number;
    expire_passwords: boolean;
    expire_days: number;
    max_auth_tries: number;
    check_history: boolean;
    check_complexity: boolean;
  };
  device_validation_settings: DeviceValidationSettings;
  user_access_without_ad: {
    enabled: boolean;
  };
  instance_observability?: InstanceObservabilityConfig;
}

export interface FormStandardImageProps {
  notification_email: string[];
}
export interface FormSchedulePublishProps {
  notification_emails: string[];
  schedule_datetime: ParsableDate;
  replace_instances: {
    block_instances: boolean;
    blocked_ttl_minutes: number;
    notify_users: boolean;
    instance_block_user_message?: string;
  };
}

export interface FormKeepingUserConnectedProps {
  operator_name: string;
  notification_email: string[];
  blocked_ttl_minutes?: number;
  notify_users?: boolean;
}

export interface FormRequestingUserReconnectionProps {
  disconnection_time: string;
  operator_name: string;
  notification_email: string[];
  user_message: string;
}

export interface FormStopTime {
  stop_time: ParsableDate;
  new_stop_time: ParsableDate;
}

export interface FormPreparationServerProps {
  instance_id: string;
  stop_time: Date | Moment | string;
  disable_auto_stop: boolean;
  no_stop: boolean;
}

export interface FormSupportServerProps {
  instance_id: string;
  include_instance_as_application_server: boolean;
  monitoring_management_enabled: boolean;
}

export type ModelLicenseType = 'BRING_YOUR_OWN_LICENSE' | 'OCI_PROVIDED';

export interface FormModelProps {
  name: string;
  description: string;
  cpu_weight: number;
  memory_weight: number;
  connection_weight: number;
  cpu_max: number;
  memory_max: number;
  connection_max: number;
  temperature_empty: number;
  temperature_stopped: number;
  temperature_operational: number;
  temperature_top: number;
  boot_time: number;
  provider_data: {
    image_id: string;
    primary_zone: string;
    secondary_zone: string;
    security_group: string;
    zone: string;
    subnet: string;
    key_pair: string;
    flavor: string;
    is_spot: boolean;
    credit_specifications: string;
    boot_volume_id: string;
    boot_volume_backup_id: string;
    license_type?: ModelLicenseType | null;
  };
}

export interface ListStatusPublicationDataProps {
  status_data: {
    state_display_text: string;
    is_robo_micro: boolean;
  };
}

export type ListEnvironmentDataProps = PaginatedResponse<EnvironmentFields>;

export type ListAllEnvironmentsDataProps = PaginatedResponse<
  AllEnvironmentsFields
>;

export type ListEnvironmentsLogsImagesFieldsDataProps = PaginatedResponse<
  EnvironmentsLogsImage
>;

export type AlarmsInstanceDataProps = PaginatedResponse<AlarmsInstanceFields>;

export type ListEnvironmentsImagesFieldsDataProps = PaginatedResponse<
  EnvironmentsImages
>;

export interface ListSessionsInInstanceFieldsProps
  extends PaginatedResponse<SessionsInInstance> {
  loading: boolean;
}

export type ListEnvironmentSessionsProps = PaginatedResponse<
  SessionsInInstance
>;

export interface InstanceFields {
  instance_uuid: string;
  instance_id: string;
  instance_name: string;
  public_ip: string;
  private_ip: string;
  flavor: string;
  zone: string;
  temperature: number;
  cpu: number;
  memory: number;
  disk_c: number | null;
  number_of_sessions: number;
  status: string;
  is_spot: boolean;
  connection_usernames: string[];
  preparation_server_instance_id: string;
  number_max_of_sessions: number;
  zero_trust: {
    state:
      | 'grace_period'
      | 'failed'
      | 'disabled'
      | 'enabled'
      | 'starting'
      | null;
  };
  status_details: {
    start_datetime: string;
    operational_datetime: string;
    operational_latency_seconds: number;
    sessions_timeout_alert: boolean;
  };
  platform_version: string;
  cpu_benchmark: {
    state: 'disabled' | 'enabled' | 'platform_old_version';
    result: number | null;
    finished_at: string | null;
    duration_seconds: number | null;
    execution_interval_minutes: number | null;
  };
}

export interface ListInstanceFieldsDataProps
  extends PaginatedResponse<InstanceFields> {
  loading: boolean;
}

export type InstanceObservabilityStatus = 'healthy' | 'warning' | 'critical';

export interface InstanceObservabilityEvent {
  type: string;
  status: InstanceObservabilityStatus | null;
  description: string | null;
  last_updated_at: string | null;
}

export interface InstanceObservabilityFields {
  status: InstanceObservabilityStatus;
  updated_at: string | null;
  events: InstanceObservabilityEvent[];
}

export interface InstanceObservabilityState {
  loading: boolean;
  error: boolean;
  data: InstanceObservabilityFields | null;
}

export interface ServerFields {
  instance_id: string;
  name: string;
  public_ip: string;
  private_ip: string;
  instance_state: InstanceStateEnum;
  image_publication_status: PublishImageEnum | InstanceStateEnum;
  has_image_publication_schedule: boolean;
  flavor: string;
  server_type: 'preparation' | 'support';
  is_application_server: boolean;
  stop_time: string;
  connection_usernames: string[];
  block_actions_enabled: boolean;
  unblock_time: string | null;
  block_reason: string;
  can_execute_actions_on_blocked_server: boolean;
  blocked_by: { first_name: string; last_name: string; email: string };
  blocked_at: string | null;
  platform_version: string | null;
  is_stopping_monitoring: boolean;
  monitoring_management_enabled: boolean;
  isLoadingMonitoringStatus: boolean;
  monitoringStatus: string;
}

export type ListServerFieldsDataProps = PaginatedResponse<ServerFields>;

export interface ListurlsDashboardDataProps {
  results: {
    results: string[];
  };
}

export interface ListSessionDatasDashboard {
  results: {
    results: [
      {
        data: any[];
        fields: string[];
      }
    ];
  };
}

export interface ProviderDataFormModel {
  image_id: string;
  primary_zone: string;
  secondary_zone: string;
  security_group: string;
  key_pair: string;
  flavor: string;
  is_spot: boolean;
  credit_specifications: string;
  boot_volume_id: string;
  boot_volume_backup_id: string;
  region?: string;
  license_type?: ModelLicenseType | null;
}

export interface ModelFormFields {
  name: string;
  description: string;
  cpu_weight: number;
  memory_weight: number;
  connection_weight: number;
  cpu_max: number;
  memory_max: number;
  connection_max: number;
  temperature_empty: number;
  temperature_stopped: number;
  temperature_operational: number;
  temperature_top: number;
  boot_time: number;
  provider_data: ProviderDataFormModel;
}

export interface ProviderDataModel {
  image_id: string;
  region: string;
  zone: string;
  security_group: string;
  subnet: string;
  flavor: string;
  license_type?: ModelLicenseType | null;
}

export interface ModelFields {
  model_uuid: string;
  name: string;
  description: string;
  creation_date: string;
  active_instances_count: number;
  active_instances_sessions_count: number;
  model_type: string;
  model_role: string;
  version: string;
  model_details_path: string;
  provider_type: string;
  provider_data?: ProviderDataModel;
}

export type ListModelFieldsDataProps = PaginatedResponse<ModelFields>;

export interface EnvironmentsLogs {
  date: string;
  time: string;
  type: string;
  message: string;
}

export interface EnvironmentsLogsImage {
  levelname: string;
  timestamp: string;
  instance_id: string;
  task_name: string;
  task_message: string;
  image_publication_start: boolean;
  publication_type: string | any;
  new_version: number;
  publication_identification: string;
  operator: string;
  operator_email: string;
}

export interface AlarmsInstanceFields {
  creation_date: string;
  status: string;
  message: string;
  operator: string;
}

export interface EnvironmentsImages {
  id: string;
  name: string;
  IP: string;
  tipo: string;
  uuid: string;
  state: string;
}

export interface SessionsInInstance {
  session_id: string;
  user: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
  };
  client: {
    client_uuid: string;
    name: string;
  };
  agent_info: {
    user_agent_info: string;
    browser_family: string;
    os_family: string;
  };
  environment_uuid: string;
  instance_id: string;
  status: string;
  login_at: string;
  logout_at: string;
  terminate_reason: string;
  instance_ip_address: string;
  user_ip_address: string;
  instance_name: string;
  virtualizer: string;
  zero_trust_connection_status:
    | 'grace_period'
    | 'failed'
    | 'disabled'
    | 'enabled'
    | 'starting'
    | 'success'
    | null;
  user_access_autosky_version: string;
}

export interface SecurityState {
  activate: boolean;
  min_length: number;
  require_upper: boolean;
  require_lower: boolean;
  require_num: boolean;
  require_symbol: boolean;
  expire_passwords: boolean;
  expire_days: number;
  max_auth_tries: number | string;
  check_history: boolean;
  check_complexity: boolean;
}

export interface EnvironmentBehaviorFieldsProps {
  first_period: {
    is_active: boolean;
    hour: string | Date;
    scale_instances_by_licences_margin: boolean | string;
    licences_margin_percent: number;
    keep_support_servers: string;
    application_server_mode: 'active' | 'stopped' | 'on_demand';
  };
  second_period: {
    is_active: boolean;
    hour: string | Date;
    scale_instances_by_licences_margin: boolean | string;
    licences_margin_percent: number;
    keep_support_servers: string;
    application_server_mode: 'active' | 'stopped' | 'on_demand';
  };
}

export interface EnvironmentMaintenanceDisplayWarningProps {
  active: boolean;
  start_date: string;
  end_date: string;
  message: string;
  display_to_logged_users: boolean;
}

export interface EnvironmentMaintenanceRemoveSessionsProps {
  enabled: boolean;
  session_removal_times_weekdays: string[];
  session_removal_times_saturday: string[];
  session_removal_times_sunday: string[];
  session_removal_max_limit: number;
  session_removal_message: string;
}
export interface EnvironmentMaintenanceBlockInstancesProps {
  enabled: boolean;
  auto_block_time: string;
}

export interface ProviderResourcesFields {
  providers: ['AWS' | 'MULTICLOUD' | 'OCI'];
  regions: {
    AWS: string[];
    MULTICLOUD: string[];
    OCI: string[];
  };
  zones: {
    AWS: any;
    MULTICLOUD: any;
    OCI: any;
  };
}
export interface ProviderResourcesProps {
  environment_uuid: null | string;
  keypairs: {
    results: null | Array<{ id: string; name: string }>;
    isLoadingKeyPairs: boolean;
  };
  flavors: {
    results: null | Array<{ name: string }>;
    isLoadingFlavors: boolean;
  };
  images: {
    results: null | Array<{ id: string; name: string; description: string }>;
    isLoadingImages: boolean;
  };
  securityGroups: {
    results: null | Array<{ id: string; name: string }>;
    isLoadingSecurityGroups: boolean;
  };
  zones: {
    results: null | Array<{
      name: string;
      state: string;
      subnet_configuration_error: string;
    }>;
    isLoadingZones: boolean;
  };
  subnets: {
    results: null | Array<{ id: string; name: string }>;
    isLoadingSubnets: boolean;
  };
  bootVolumes: {
    results: null | Array<{ id: string; name: string }>;
    isLoadingBootVolumes: boolean;
  };
  bootVolumeBackups: {
    results: null | Array<{ id: string; name: string }>;
    isLoadingBootVolumeBackups: boolean;
  };
  listProvidersResources: {
    results: null | ProviderResourcesFields;
    isLoadingListProvidersResources: boolean;
  };
}

export interface DashboardMetricsSessionsGraph {
  timestamp: number;
  licenses: number;
  sessions: {
    max: number;
    avg: number;
  };
  vacancies: {
    max: number;
    avg: number;
  };
}

export interface ListAppsFields {
  name: string;
  client_count: number;
}

export interface ListPlatformAppsFields {
  name: string;
  code: string;
  client_count: number;
}

export interface ListClientsAppsFields {
  app_uuid: string;
  name: string;
  state: 'active' | 'inactive';
  client: { client_uuid: string; name: string };
}

export interface ListClientsPlatformAppsFields {
  code: string;
  name: string;
  state: 'attached' | 'detached';
  client: { client_uuid: string; name: string };
}

export interface ListClientsToIncludeFields {
  client_uuid: string;
  name: string;
}

export interface DashboardSessionsGraphFields {
  metrics: DashboardMetricsSessionsGraph[];
}

export interface DashboardEnvironmentsCostGraph {
  timestamp: number;
  cost: string;
}

export interface DashboardEnvironmentsCostGraphFields {
  total: string;
  metrics: DashboardEnvironmentsCostGraph[];
}

export enum PeriodCountAccess {
  LAST_24_HOURS = '1d',
  LAST_48_HOURS = '2d',
  LAST_7_DAYS = '7d',
  LAST_15_DAYS = '15d',
  LAST_30_DAYS = '30d',
  LAST_60_DAYS = '60d',
}

export interface DateFilterFields {
  'start-date': string;
  'end-date': string;
}

export enum AvgMaxCountAccess {
  MAX = 'max',
  AVG = 'avg',
}

export enum ModelTypeEnum {
  customImage = 'custom_image',
  bootVolume = 'boot_volume',
  bootVolumeBackup = 'boot_volume_backup',
}

export enum PublishImageEnum {
  STOPPING = 'STOPPING',
  GENERATING = 'GENERATING',
  PUBLISHING = 'PUBLISHING',
  REPLACING = 'REPLACING',
  BLOCKING = 'BLOCKING',
  DISCONNECTING = 'DISCONNECTING',
  ERROR = 'ERROR',
  FINISHED = 'FINISHED',
  INITIALIZING = 'INITIALIZING',
  PENDING = 'PENDING',
  NOT_FOUND = 'NOT_FOUND',
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  TIMEOUT = 'TIMEOUT',
  SKIPPED = 'SKIPPED',
  WARNING = 'WARNING',
}

export enum InstanceStateEnum {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  STOPPING = 'STOPPING',
  STOPPED = 'STOPPED',
  TERMINATED = 'TERMINATED',
  WARNING = 'WARNING',
}

export enum VirtualizerEnum {
  CONNECT = 'connect',
  CONNECT_DIRECT = 'connect_direct',
  RDP = 'rdp',
  RDP_DIRECT = 'rdp_direct',
  RDP_APP = 'rdp_app',
  TSPLUS = 'tsplus',
  TSPLUS_REMOTE_APP = 'tsplus_remote_app',
  TSPLUS_ALL = 'tsplus_all',
  ASPW = 'aspw',
  ASPW_ALL = 'aspw_all',
}

export enum EnvironmentBehaviorEnum {
  period = 'period',
  manual = 'manual',
  micro = 'micro',
  // eslint-disable-next-line @typescript-eslint/camelcase
  spot_period = 'spot_period',
}

export interface FlavorsCostFields {
  flavor: string;
  total_cost: string;
  num_instances: number;
  cost_percentage: string;
}

export type ListFlavorsCost = PaginatedResponse<FlavorsCostFields>;

export type ListAppsProps = PaginatedResponse<ListAppsFields>;

export type ListPlatformAppsProps = PaginatedResponse<ListPlatformAppsFields>;

export type ListClientsAppsProps = PaginatedResponse<ListClientsAppsFields>;

export type ListClientsPlatformAppsProps = PaginatedResponse<
  ListClientsPlatformAppsFields
>;

export type ListClientsToIncludeProps = PaginatedResponse<
  ListClientsToIncludeFields
>;

export interface CPUBenchmarksSettings {
  enabled: boolean;
  execution_interval_minutes: number;
  duration_seconds: number;
}

export interface EnvironmentMonitoringProps {
  enabled: boolean;
  cpu_benchmarks: CPUBenchmarksSettings;
}

export interface ListEnvironmentHistoryFields {
  created_at: Date;
  monitoring_type: string;
  vacancies_count: number;
  licenses_count: number;
  operational_timing_expected: number;
  median_seconds: number | null;
  sample_count: number | null;
  instance_id: string;
  instance_details: {
    flavor: string;
    region: string;
    sessions_count: number;
    platform_version: string | null;
    zero_trust_state:
      | 'grace_period'
      | 'failed'
      | 'disabled'
      | 'enabled'
      | 'starting'
      | 'success'
      | null;
    seconds_spent_to_operational_or_alert: number;
    expired_sessions: string | null;
    blocked_by_monitoring: boolean;
  };
}

export type ListEnvironmentHistoryProps = PaginatedResponse<
  ListEnvironmentHistoryFields
>;

export interface PerformanceCPUGraph {
  timestamp: number;
  cpu: {
    max: number;
    avg: number;
  };
}

export interface PerformanceMemoryGraph {
  timestamp: number;
  memory: {
    max: number;
    avg: number;
  };
}

export interface PerformanceOperationalTimeGraph {
  timestamp: number;
  operational_time: {
    max: number;
    avg: number;
  };
}
export interface PerformanceCPUGraphFields {
  metrics: PerformanceCPUGraph[];
}

export interface PerformanceMemoryGraphFields {
  metrics: PerformanceMemoryGraph[];
}

export interface PerformanceOperationalTimeGraphFields {
  metrics: PerformanceOperationalTimeGraph[];
}

export interface PublicationScheduleFields {
  schedule_uuid: string;
  notification_emails: string[];
  status: string;
  schedule_datetime: string;
  timezone: string;
  request_uuid: string;
  request_datetime: string;
  operator_email: string;
}

export type ListPublicationScheduleProps = PaginatedResponse<
  PublicationScheduleFields
>;

export interface BulkClientsFields {
  client_uuid: string;
  environment_uuid: string;
  name: string;
  email_admin: {
    email_admin: string;
    replace_all: boolean;
  };
  cli_uuids: string[];
  copy_admin: boolean;
  block_same_user_multiple_session: boolean;
  use_ios_devices: boolean;
  user_mfa_is_active: boolean;
  user_connection: {
    virtualizer: string;
    connect_port: number;
    web_port: number;
  };
  license_limit_configuration: {
    has_license_limit: boolean;
    license_limit: number;
    available_licenses: number;
  };
  connection_type_configuration: {
    connection_type_enabled: boolean;
    connection_type: 'public' | 'private' | null;
  };
}

export type ListBulkClientsProps = PaginatedResponse<BulkClientsFields>;

export interface StatusReportProps {
  demonstration_environment_count: number;
  deployment_environment_count: number;
  development_environment_count: number;
  homologation_environment_count: number;
  migration_environment_count: number;
  pending_environment_count: number;
  production_environment_count: number;
}

export interface ProductReportProps {
  autosky: {
    total_count: number;
    manual_count: number;
    period_count: number;
    micro_count: number;
    spot_period_count: number;
  };
  business_one: {
    total_count: number;
    ccc_count: number;
    haas_count: number;
  };
}
export interface ProductTypeReportProps {
  dedicated: number;
  shared: number;
  manual: number;
  period: number;
  micro: number;
  spot_period: number;
  ccc: number;
  haas: number;
}

export interface ConnectionTypeEnvironmentReportProps {
  public: number;
  private: number;
}

export interface ZeroTrustReportProps {
  activate: number;
  deactivate: number;
}

export interface MaintenanceReportProps {
  in_maintenance: number;
  future_maintenance: number;
  display_warning: number;
}

export interface BehaviorReportProps {
  total: number;
  manual: number;
  period: number;
  period_spot: number;
  micro: number;
}

export interface VirtualizerReportProps {
  connect: number;
  connect_direct: number;
  rdp: number;
  rdp_direct: number;
  rdp_app: number;
  tsplus: number;
  tsplus_remote_app: number;
  tsplus_all: number;
  aspw: number;
  aspw_all: number;
}
export interface ProviderReportProps {
  aws: number;
  oci: number;
  multicloud: number;
}

export interface DNSReportProps {
  activate: number;
  deactivate: number;
}
export interface RegionReportProps {
  regions_count: { [key: string]: number };
}

export interface TimezonesFields {
  timezones: string[];
}

export interface ValidateZeroTrustFields {
  environment_config: { enabled: boolean };
  platform_version: {
    valid: boolean;
    minimum_version: string;
    current_version: string;
  };
  provider_rule: { applicable: boolean; valid: boolean };
  integration_test: { valid: boolean; error: string | null };
}

export interface ZeroTrustStateProps {
  status:
    | 'grace_period'
    | 'failed'
    | 'disabled'
    | 'enabled'
    | 'starting'
    | 'success'
    | null;
}

export interface PublishImageProps {
  instance_ids?: string[];
  stop_server: boolean;
  notify_list: string;
  replace_instances: {
    block_instances: boolean;
    instance_block_user_message: string;
    blocked_ttl_minutes: number;
    notify_users: boolean;
  };
}

interface Operator {
  first_name: string;
  last_name: string;
  email: string;
}

export interface Step {
  id: string;
  name: string;
  description: string | null;
  details: string | Record<string, unknown> | null;
  status: string;
  started_at: string;
  finished_at: string | null;
  duration_milliseconds: number | null;
  queue_duration_milliseconds: number;
  parent: string | null;
  sub_steps: Step[];
}

export interface Publication {
  uuid: string;
  name: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  model_version: string;
  duration_milliseconds: number | null;
  operator: Operator;
  steps: Step[];
}

export interface PublicationTemplateStep {
  name: string;
  duration_milliseconds: number;
}

export interface PublicationResponse {
  publication_template_steps: PublicationTemplateStep[];
  publications: Publication[];
}

export interface LicenseGroupClient {
  name: string;
  uuid: string;
}

export interface LicenseGroupsFields {
  name: string;
  license_limit: number;
  created_at: string | null;
  updated_at: string | null;
  uuid: string;
  clients: LicenseGroupClient[];
  clients_count: number;
  available_licenses_to_group: number;
}

export interface ListLicenseGroupsProps
  extends PaginatedResponse<LicenseGroupsFields> {
  available_licenses_new_group: number;
}

export interface AdminPinProps {
  environment_admin_pin: string;
}

export interface BlockDurationSelectProps {
  value?: number;
  onChange: (value: number) => void;
  notifyUsers?: boolean;
  onChangeNotifyUsers: (value: boolean) => void;
}

export enum ServerMonitoringStatusEnum {
  DOWN = 'down',
  UP = 'up',
  TROUBLE = 'trouble',
  CRITICAL = 'critical',
  SUSPENDED = 'suspended',
  MAINTENANCE = 'maintenance',
  DISCOVERY = 'discovery',
  CONFIGURATION_ERROR = 'configuration_error',
  WITHOUT_MONITOR = 'without_monitor',
  UNAVAILABLE = 'unavailable',
}

export interface EnvironmentsState {
  readonly environmentList: ListEnvironmentDataProps;
  readonly environmentsList: ListAllEnvironmentsDataProps;
  readonly environmentSelected: EnvironmentFields | null;
  readonly instancesList: ListInstanceFieldsDataProps;
  readonly preparationServer: ServerFields;
  readonly isLoadingPreparationServer: boolean;
  readonly supportServers: ListServerFieldsDataProps;
  readonly isLoadingSupportServers: boolean;
  readonly modelsList: ListModelFieldsDataProps;
  readonly modelSelected: ModelFormFields | null;
  readonly imagensList: ListEnvironmentsImagesFieldsDataProps;
  readonly securityState: SecurityState;
  readonly isLoadingSecurityState: boolean;
  readonly isLoading: boolean;
  readonly sessionsInInstanceList: ListSessionsInInstanceFieldsProps;
  readonly environmentBehaviorWeek: EnvironmentBehaviorFieldsProps;
  readonly environmentBehaviorSaturday: EnvironmentBehaviorFieldsProps;
  readonly environmentBehaviorSunday: EnvironmentBehaviorFieldsProps;
  readonly providerResources: ProviderResourcesProps;
  readonly urlsDashboard: ListurlsDashboardDataProps;
  readonly sessionDatasDashboard: ListSessionDatasDashboard;
  readonly instanceDatasDashboard: ListSessionDatasDashboard;
  readonly environmentMaintenanceDisplayWarning: EnvironmentMaintenanceDisplayWarningProps;
  readonly environmentMaintenanceRemoveSessions: EnvironmentMaintenanceRemoveSessionsProps;
  readonly environmentMaintenanceBlockInstances: EnvironmentMaintenanceBlockInstancesProps;
  readonly imagensLogsList: ListEnvironmentsLogsImagesFieldsDataProps;
  readonly isLoadingMaintenance: boolean;
  readonly isLoadingLogs: boolean;
  readonly alarmsInstanceList: AlarmsInstanceDataProps;
  readonly dashboardSessionsGraph: DashboardSessionsGraphFields;
  readonly dashboardEnvironmentCostGraph: DashboardEnvironmentsCostGraphFields;
  readonly periodFilter: PeriodCountAccess;
  readonly performanceDateFilter: DateFilterFields;
  readonly periodFilterCosts: PeriodCountAccess;
  readonly environmentCostPeriodFilter: PeriodCountAccess;
  readonly flavorsCostList: ListFlavorsCost;
  readonly flavorsCostPeriodFilter: PeriodCountAccess;
  readonly flavorsCostIsLoading: boolean;
  readonly isLoadingApps: boolean;
  readonly applicationsList: ListAppsProps;
  readonly isLoadingClientsApps: boolean;
  readonly clientsApplicationsList: ListClientsAppsProps;
  readonly clientsPlatformApplicationsList: ListClientsPlatformAppsProps;
  readonly clientsToIncludeList: ListClientsToIncludeProps;
  readonly applicationsPlatformList: ListPlatformAppsProps;
  readonly migrateErrors: string[];
  readonly environmentAvgMaxFilterCPU: AvgMaxCountAccess;
  readonly environmentAvgMaxFilterMemory: AvgMaxCountAccess;
  readonly environmentAvgMaxFilterOperationalTime: AvgMaxCountAccess;
  readonly environmentPerformanceCPU: PerformanceCPUGraphFields;
  readonly environmentPerformanceMemory: PerformanceMemoryGraphFields;
  readonly environmentPerformanceOperationalTime: PerformanceOperationalTimeGraphFields;
  readonly environmentMonitoring: EnvironmentMonitoringProps;
  readonly monitoringHistory: ListEnvironmentHistoryProps;
  readonly publicationSchedule: ListPublicationScheduleProps;
  readonly bulkClients: ListBulkClientsProps;
  readonly isLoadingPublicationSchedule: boolean;
  readonly isLoadingBulkClients: boolean;
  readonly selectedEnvironment: EnvironmentFields | null;
  readonly behaviorReport: BehaviorReportProps;
  readonly isLoadingBehaviorReport: boolean;
  readonly DNSReport: DNSReportProps;
  readonly isLoadingDNSReport: boolean;
  readonly maintenanceReport: MaintenanceReportProps;
  readonly isLoadingMaintenanceReport: boolean;
  readonly productReport: ProductReportProps;
  readonly isLoadingProductReport: boolean;
  readonly productTypeReport: ProductTypeReportProps;
  readonly isLoadingProductTypeReport: boolean;
  readonly providerReport: ProviderReportProps;
  readonly isLoadingProviderReport: boolean;
  readonly regionReport: RegionReportProps;
  readonly isLoadingRegionReport: boolean;
  readonly statusReport: StatusReportProps;
  readonly isLoadingStatusReport: boolean;
  readonly virtualizerReport: VirtualizerReportProps;
  readonly isLoadingVirtualizerReport: boolean;
  readonly zeroTrustReport: ZeroTrustReportProps;
  readonly isLoadingZeroTrustReport: boolean;
  readonly connectionTypeEnvironmentReport: ConnectionTypeEnvironmentReportProps;
  readonly isLoadingConnectionTypeEnvironmentReport: boolean;
  readonly timezonesList: TimezonesFields;
  readonly selectedTimezone: TimezonesFields | null;
  readonly validateZeroTrust: ValidateZeroTrustFields;
  readonly publishHistory: PublicationResponse;
  readonly environmentSessionsList: ListEnvironmentSessionsProps;
  readonly isLoadingEnvironmentSessionsList: boolean;
  readonly listLicenseGroups: ListLicenseGroupsProps;
  readonly licenseGroupDetails: LicenseGroupsFields;
  readonly isLoadingLicenseGroups: boolean;
  readonly environmentAdvisorsList: ListAdvisorsDataProps;
  readonly isLoadingEnvironmentAdvisorsList: boolean;
  readonly adminPin: AdminPinProps;
  readonly isLoadingAdminPin: boolean;
  readonly instanceObservability: InstanceObservabilityState;
}
